/**
 * SeatGeek API Integration Service
 * Provides methods to fetch events, performers, and venues from SeatGeek API
 */

const EVENT_DISCOVERY_BASE_URL = '/api/event-discovery';

/**
 * Make authenticated request to SeatGeek API
 */
const seatgeekRequest = async (endpoint, params = {}) => {
  try {
    const sectionInfoMatch = endpoint.match(/^\/events\/section_info\/(\d+)$/);
    const resourceMatch = endpoint.match(/^\/(events|performers|venues)$/);
    const path = sectionInfoMatch
      ? `${EVENT_DISCOVERY_BASE_URL}/section-info/${sectionInfoMatch[1]}`
      : resourceMatch
        ? `${EVENT_DISCOVERY_BASE_URL}/${resourceMatch[1]}`
        : null;
    if (!path) throw new Error('Unsupported SeatGeek endpoint');
    const url = new URL(path, window.location.origin);

    // Add additional parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });

    const response = await fetch(url.toString());

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `SeatGeek API error: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    // Validate that the response is actually JSON before parsing
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new Error(
        `SeatGeek API returned unexpected content-type: ${contentType} (expected JSON)`
      );
    }

    const data = await response.json();

    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch events from SeatGeek
 * @param {Object} options - Search options
 * @param {string} options.q - Search query
 * @param {string} options.taxonomies.name - Event category (concert, sports, etc.)
 * @param {string} options.venue.city - City filter
 * @param {string} options.venue.state - State filter
 * @param {number} options.per_page - Results per page (default: 25, max: 1000)
 * @param {number} options.page - Page number
 * @param {string} options.sort - Sort order (score.desc, datetime_utc.asc, etc.)
 */
export const fetchSeatGeekEvents = async (options = {}) => {
  const {
    q,
    taxonomies_name,
    venue_city,
    venue_state,
    per_page = 25,
    page = 1,
    sort = 'score.desc',
    ...otherParams
  } = options;

  const params = {
    q,
    'taxonomies.name': taxonomies_name,
    'venue.city': venue_city,
    'venue.state': venue_state,
    per_page,
    page,
    sort,
    ...otherParams,
  };

  const data = await seatgeekRequest('/events', params);
  return data;
};

/**
 * Fetch performers from SeatGeek
 * @param {Object} options - Search options
 * @param {string} options.q - Search query
 * @param {string} options.taxonomies.name - Performer category
 * @param {number} options.per_page - Results per page
 * @param {number} options.page - Page number
 */
export const fetchSeatGeekPerformers = async (options = {}) => {
  const { q, taxonomies_name, per_page = 25, page = 1, ...otherParams } = options;

  const params = {
    q,
    'taxonomies.name': taxonomies_name,
    per_page,
    page,
    ...otherParams,
  };

  const data = await seatgeekRequest('/performers', params);
  return data;
};

/**
 * Fetch venues from SeatGeek
 * @param {Object} options - Search options
 * @param {string} options.q - Search query
 * @param {string} options.city - City filter
 * @param {string} options.state - State filter
 * @param {number} options.per_page - Results per page
 * @param {number} options.page - Page number
 */
export const fetchSeatGeekVenues = async (options = {}) => {
  const { q, city, state, per_page = 25, page = 1, ...otherParams } = options;

  const params = {
    q,
    city,
    state,
    per_page,
    page,
    ...otherParams,
  };

  const data = await seatgeekRequest('/venues', params);
  return data;
};

/**
 * Fetch section and row information for a specific event
 * @param {string|number} eventId - SeatGeek event ID
 * @returns {Promise<Object>} Section and row information for the event
 */
export const fetchEventSectionInfo = async eventId => {
  try {
    const data = await seatgeekRequest(`/events/section_info/${eventId}`);

    return data;
  } catch (error) {
    console.error('🎪 ERROR FETCHING SECTION INFO for event', eventId, ':', error);
    throw error;
  }
};

/**
 * Fetch popular/featured events for landing page
 * Gets high-scoring events from major categories
 */
export const fetchFeaturedEvents = async () => {
  try {
    // Fetch popular events from different categories
    const [concerts, sports, theater] = await Promise.all([
      fetchSeatGeekEvents({
        taxonomies_name: 'concert',
        per_page: 10,
        sort: 'score.desc',
      }),
      fetchSeatGeekEvents({
        taxonomies_name: 'sports',
        per_page: 10,
        sort: 'score.desc',
      }),
      fetchSeatGeekEvents({
        taxonomies_name: 'theater',
        per_page: 5,
        sort: 'score.desc',
      }),
    ]);

    // Combine and mix the results
    const allEvents = [
      ...(concerts.events || []),
      ...(sports.events || []),
      ...(theater.events || []),
    ];

    // Sort by score and take top events
    const featuredEvents = allEvents.sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 20);

    return featuredEvents;
  } catch (error) {
    return [];
  }
};

/**
 * Transform SeatGeek event to Sharetribe listing format
 * This allows SeatGeek events to be displayed in carousel components
 */
/**
 * Format date for display (e.g., "FEB 15")
 */
const formatEventDate = dateString => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const day = date.getDate();
    return { month, day, fullDate: dateString };
  } catch (error) {
    console.warn('Error formatting date:', dateString, error);
    return null;
  }
};

export const transformSeatGeekEventToListing = event => {
  const { id, title, datetime_utc, datetime_local, venue, performers, stats, url, score } = event;

  // Extract date information directly
  let eventDate = 'Date TBD';
  let eventDateNum = 'TBD';

  if (datetime_local || datetime_utc) {
    try {
      const date = new Date(datetime_local || datetime_utc);
      if (!isNaN(date.getTime())) {
        eventDate = date.toLocaleDateString('en-US', { weekday: 'short' }); // "Sat"
        eventDateNum = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); // "Aug 10"
      }
    } catch (e) {
      console.warn('Error parsing SeatGeek date:', datetime_local || datetime_utc, e);
    }
  }

  // Get primary performer image or venue image
  const primaryPerformer = performers?.[0];
  const eventImage = primaryPerformer?.image || venue?.image || null;

  // Format price from stats
  const averagePrice = stats?.average_price;
  const lowestPrice = stats?.lowest_price;
  const displayPrice = lowestPrice || averagePrice;

  // Format venue information
  const venueName = venue?.name || 'TBA';
  const venueLocation =
    venue?.city && venue?.state
      ? `${venue.city}, ${venue.state}`
      : venue?.city || venue?.state || '';

  return {
    id: {
      uuid: `seatgeek-${id}`,
    },
    type: 'listing',
    attributes: {
      title,
      description: `${title} at ${venueName}`,
      geolocation: venue?.location
        ? {
            lat: venue.location.lat,
            lng: venue.location.lon,
          }
        : null,
      price: displayPrice
        ? {
            amount: Math.round(displayPrice * 100), // Convert to cents
            currency: 'USD',
          }
        : null,
      publicData: {
        // Dynamic event data
        eventDate: {
          fullDate: datetime_local || datetime_utc,
          month: eventDateNum.split(' ')[0] || 'TBD',
          day: eventDateNum.split(' ')[1] || 'TBD',
          weekday: eventDate || 'TBD',
        },
        source: 'seatgeek',
        imageUrl: eventImage,
        venueName: venueName,
        venueLocation: venueLocation,
        ticketUrl: url, // SeatGeek specific event URL from API response

        // Additional event details
        eventDateTime: datetime_utc,
        eventDateTimeLocal: datetime_local,
        performers: performers?.map(p => p.name).join(', '),
        category: 'event-ticket',
        externalUrl: url,
        score: score,
      },
    },
    images: eventImage
      ? [
          {
            id: { uuid: `img-${id}` },
            type: 'image',
            attributes: {
              variants: {
                'landscape-crop': { url: eventImage },
                'landscape-crop2x': { url: eventImage },
              },
            },
          },
        ]
      : [],
    relationships: {},
    // Add simple date attributes like other attributes
    date: eventDate,
    dateNum: eventDateNum,
  };
};

export default {
  fetchSeatGeekEvents,
  fetchSeatGeekPerformers,
  fetchSeatGeekVenues,
  fetchFeaturedEvents,
  transformSeatGeekEventToListing,
};
